export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Adapted from https://stackoverflow.com/a/6178341/788004
export function isValidDate(dateString: string, format: string = 'MM/DD/YYYY') {
  // Making sure it is of right format
  const regexString = new RegExp(`^${format.replace(/MM|DD/g, '(\\d{1,2})').replace('YYYY', '(\\d{4})')}$`);
  const match = dateString.match(regexString);
  if (!match || match.length !== 4) {
    return false;
  }

  // Extract the month, day and year from the string based on the format
  const tokenOrder = ['MM', 'DD', 'YYYY'].sort((a, b) => (
    format.indexOf(a) < format.indexOf(b) ? -1 : 1
  ));
  let monthStr = '';
  let dayStr = '';
  let yearStr = '';
  for (let i = 0; i < tokenOrder.length; i++) {
    if (tokenOrder[i] === 'MM') {
      monthStr = match[i + 1];
    } else if (tokenOrder[i] === 'DD') {
      dayStr = match[i + 1];
    } else if (tokenOrder[i] === 'YYYY') {
      yearStr = match[i + 1];
    }
  }

  // Making sure month is within range
  const month = parseInt(monthStr, 10);
  if (month === 0 || month > 12) {
    return false;
  }

  // Check for leap years
  const year = parseInt(yearStr, 10);
  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
    monthLength[1] = 29;
  }

  const day = parseInt(dayStr, 10);
  return day > 0 && day <= monthLength[month - 1];
}

// We want to standardize on a date format (MM/DD/YYYY) for internal use
export function getInternalFormat(dateStr: string, currentFormat: string) {
  // Making sure it is of right format
  const regexString = new RegExp(`^${currentFormat.replace(/MM|DD/g, '(\\d{1,2})').replace('YYYY', '(\\d{4})')}$`);
  const match = dateStr.match(regexString);

  // Extract the month, day and year from the string based on the format
  const tokenOrder = ['MM', 'DD', 'YYYY'].sort((a, b) => (
    currentFormat.indexOf(a) < currentFormat.indexOf(b) ? -1 : 1
  ));
  let monthStr = '';
  let dayStr = '';
  let yearStr = '';
  for (let i = 0; i < tokenOrder.length; i++) {
    if (tokenOrder[i] === 'MM') {
      monthStr = match[i + 1];
    } else if (tokenOrder[i] === 'DD') {
      dayStr = match[i + 1];
    } else if (tokenOrder[i] === 'YYYY') {
      yearStr = match[i + 1];
    }
  }

  return `${monthStr}/${dayStr}/${yearStr}`;
}

export function getInternalFormatFromDate(date: Date) {
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

export function getDateFromInternalFormat(dateStr: string) {
  const [month, day, year] = dateStr.split('/');
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
}

export function isDateString(dateText: string, format: string) {
  return dateText && isValidDate(dateText, format);
}

export function formatDate(date: Date, format: string) {
  return format
    .replace('MM', `${date.getMonth() + 1}`)
    .replace('DD', `${date.getDate()}`)
    .replace('YYYY', `${date.getFullYear()}`);
}

export const getFirstDayOfMonth = (year: number, month: number) => (
  new Date(year, month, 1, 0, 0, 0, 0)
);

export const getLastDayOfMonth = (year: number, month: number) => (
  new Date(year, month + 1, 0, 23, 59, 59, 999)
);

export const getFirstDayOfMonthWithDate = (date: Date) => (
  getFirstDayOfMonth(date.getFullYear(), date.getMonth())
);

export const getLastDayOfMonthWithDate = (date: Date) => (
  getLastDayOfMonth(date.getFullYear(), date.getMonth())
);

// Checks if two dates have the same date
export const isSameDate = (date1: Date, date2: Date) => {
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();
  const day1 = date1.getDate();

  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();
  const day2 = date2.getDate();

  return (
    year1 === year2
    && month1 === month2
    && day1 === day2
  );
};

// Checks if date is between startDate and endDate (inclusively)
export const isBetweenDate = (date: Date, startDate: Date, endDate: Date) => {
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    0, 0, 0, 0,
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
    23, 59, 59, 999,
  );
  const time = date.getTime();
  return time >= start.getTime() && time <= end.getTime();
};

// Checks if date1 is before date2
export const isBeforeDate = (date1: Date, date2: Date) => {
  const adjustedDate1 = new Date(
    date1.getFullYear(),
    date1.getMonth(),
    date1.getDate(),
    0, 0, 0, 0,
  );
  const adjustedDate2 = new Date(
    date2.getFullYear(),
    date2.getMonth(),
    date2.getDate(),
    0, 0, 0, 0,
  );
  return adjustedDate1.getTime() < adjustedDate2.getTime();
};

// Checks if date1 is after date2
export const isAfterDate = (date1: Date, date2: Date) => isBeforeDate(date2, date1);

export default null;